import React, { FC, useEffect, useMemo } from 'react'
import { Button, Modal } from 'antd'
import { ModalProps } from 'antd/lib/modal'

import {
	change, getFormValues, initialize, submit
} from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
// assets
import { useTranslation } from 'react-i18next'
import { LockOutlined } from '@ant-design/icons'
import { ReactComponent as CloseIcon } from '../../../assets/icons/BaseIcons/x-icon.svg'
import { FORMS, STORE_ITEM_TRANSACTION_TYPE, translateStoreItemTitleTransactionTypeToSk } from '../../../utils/enums'
// eslint-disable-next-line import/no-cycle
import CreateStoreItemTransactionForm from '../forms/CreateStoreItemTransactionForm'
// eslint-disable-next-line import/no-cycle
import { createStoreItemTransactions, createStoreItemTransactionViaStore, ICreateStoreItemTransactionForm } from '../../../reducers/storeItemTransactions/actions'
// eslint-disable-next-line import/no-cycle
import { ContextStore, LockedSide } from '../types'
// eslint-disable-next-line import/no-cycle
import { STORE_TYPE } from '../../stores/Stores'

// eslint-disable-next-line import/no-cycle

export interface CreateTransactionProps {
	amount: number,
	type: STORE_ITEM_TRANSACTION_TYPE,
	storeItemName: string,
	storeItemID: number,
	jobToID: number | null | undefined,
}

type Props = ModalProps & {
	isVisible: boolean,
	transaction: CreateTransactionProps | null,
	onCancel: Function | React.MouseEvent<HTMLElement, MouseEvent>,
	onSuccess: Function,
	classNameBtn?: string,
	contextStore?: ContextStore | null
}

const CreateStoreItemTransactionModal: FC<Props> = (props) => {
	const {
		isVisible,
		onCancel,
		onSuccess,
		transaction,
		width,
		classNameBtn,
		contextStore
	} = props

	const { t } = useTranslation('pages')
	const [transactionType, setTransactionType] = React.useState<STORE_ITEM_TRANSACTION_TYPE>(STORE_ITEM_TRANSACTION_TYPE.REGULAR)
	// strana, na ktorej kontextový sklad práve sedí (mení sa tlačidlom výmeny)
	const [lockSide, setLockSide] = React.useState<Exclude<LockedSide, null>>(contextStore?.defaultSide || 'FROM')
	// používateľ vedome odomkol transakciu -> obe adresy sú voľné
	const [isUnlocked, setIsUnlocked] = React.useState(false)
	// ochrana proti dvojkliku - kým beží vytvorenie transakcie, ďalšie odoslanie sa zahodí (inak vzniknú 2 transakcie)
	const [isSubmitting, setIsSubmitting] = React.useState(false)
	// ref drží stav synchronne (state sa prejaví až po re-renderi, rýchly dvojklik by ho nestihol vidieť)
	const isSubmittingRef = React.useRef(false)

	const dispatch: any = useDispatch()

	const formValues: ICreateStoreItemTransactionForm = useSelector((state) => getFormValues(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM)(state)) as ICreateStoreItemTransactionForm

	/**
	 * Mód "Pripísať cez sklad": IN transakcia smie ísť len do virtuálneho skladu, takže pripísanie
	 * priamo na zákazku sa skladá z dvoch transakcií (IN do skladu + prevod na zákazku).
	 * Ak je kontextom už virtuálny sklad, pripisuje sa doňho priamo a nič sa nemení.
	 *
	 * Vyžadujeme známy typ skladu - detail skladu (/sklady/detail) typ neposiela a býva to práve
	 * virtuálny sklad, tam sa preto držíme dnešného správania.
	 */
	const isViaStoreMode = !!contextStore?.id
		&& !!contextStore?.label
		&& !!contextStore?.storeType
		&& contextStore.storeType !== STORE_TYPE.VIRTUAL_STORE
		&& transactionType === STORE_ITEM_TRANSACTION_TYPE.IN

	/**
	 * Nezamykáme bez labelu - zamknuté pole zobrazuje adresu z kontextu, nie zo zoznamu adries.
	 * Pri "Odpísať" je jediná adresa "Adresa z", pri "Pripísať" je cieľom kontextový sklad
	 * (buď priamo, ak je virtuálny, alebo cez sklad zvolený v poli "Pripísať cez").
	 */
	const lockedSide: LockedSide = useMemo(() => {
		if (!contextStore?.id || !contextStore?.label) {
			return null
		}
		// v móde cez sklad je cieľ pevne daný zákazkou a odomknúť sa nedá
		if (isViaStoreMode) {
			return 'TO'
		}
		if (isUnlocked) {
			return null
		}
		if (transactionType === STORE_ITEM_TRANSACTION_TYPE.CONSUME) {
			return 'FROM'
		}
		// mimo módu cez sklad smie IN skončiť len vo virtuálnom sklade - inde nezamykáme
		if (transactionType === STORE_ITEM_TRANSACTION_TYPE.IN) {
			return contextStore.storeType === STORE_TYPE.VIRTUAL_STORE ? 'TO' : null
		}
		return lockSide
	}, [contextStore, isUnlocked, transactionType, lockSide, isViaStoreMode])

	useEffect(() => {
		const defaultSide = contextStore?.defaultSide || 'FROM'
		// pri (znovu)otvorení dialógu vždy začíname s odomknutým odosielaním
		isSubmittingRef.current = false
		setIsSubmitting(false)
		setLockSide(defaultSide)
		setIsUnlocked(false)
		setTransactionType(transaction?.type || STORE_ITEM_TRANSACTION_TYPE.REGULAR)

		dispatch(initialize(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, {
			...transaction,
			jobFromID: contextStore && defaultSide === 'FROM' ? contextStore.id : null,
			jobToID: contextStore && defaultSide === 'TO' ? contextStore.id : transaction?.jobToID,
			viaJobID: null,
			useExistingStock: false,
			viaStoreStock: null
		}))
	}, [dispatch, isVisible])

	// kontextový sklad drží svoju stranu aj po výmene alebo po zmene typu transakcie
	useEffect(() => {
		if (!isVisible || !contextStore || !lockedSide) {
			return
		}
		if (lockedSide === 'FROM') {
			dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobFromID', contextStore.id))
			if (formValues?.jobToID === contextStore.id) {
				dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobToID', null))
			}
		} else {
			dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobToID', contextStore.id))
			if (formValues?.jobFromID === contextStore.id) {
				dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobFromID', null))
			}
		}
	}, [lockedSide, isVisible])

	const handleSwap = () => {
		const { jobFromID, jobToID } = formValues || {}
		dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobFromID', jobToID ?? null))
		dispatch(change(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, 'jobToID', jobFromID ?? null))
		setLockSide((prev) => (prev === 'FROM' ? 'TO' : 'FROM'))
	}

	const handleUnlockRequest = () => {
		Modal.confirm({
			title: t('pages:Odomknúť transakciu?'),
			content: t('pages:Používateľ bude môcť zvoliť zákazku/sklad, ktorý sa netýka tohoto skladu.'),
			icon: <LockOutlined style={{ color: '#FF6B00' }}/>,
			okText: t('pages:Odomknúť'),
			cancelText: t('pages:Zrušiť'),
			centered: true,
			onOk: () => setIsUnlocked(true)
		})
	}

	const handleRelock = (side: Exclude<LockedSide, null>) => {
		setLockSide(side)
		setIsUnlocked(false)
	}

	const handleCreate = (body: ICreateStoreItemTransactionForm) => {
		dispatch(createStoreItemTransactions(body, () => {
			// úspech: flag zámerne nereset-ujeme - modál sa zatvorí cez onSuccess a reset spraví efekt pri ďalšom otvorení
			onSuccess()
			dispatch(initialize(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, {
				...transaction
			}))
		}, () => {
			// zlyhanie: odomkneme, aby používateľ mohol skúsiť znova
			isSubmittingRef.current = false
			setIsSubmitting(false)
		}))
	}

	/**
	 * Pripísanie cez sklad je jedno volanie - server z neho spraví dve transakcie
	 * (IN do skladu + prevod na zákazku) v jednej DB transakcii.
	 */
	const handleCreateViaStore = (values: ICreateStoreItemTransactionForm) => {
		dispatch(createStoreItemTransactionViaStore({
			amount: values.amount,
			storeItemID: values.storeItemID,
			viaJobID: values.viaJobID as number,
			jobToID: values.jobToID as number,
			useExistingStock: !!values.useExistingStock
		}, () => {
			// úspech: flag zámerne nereset-ujeme - modál sa zatvorí cez onSuccess a reset spraví efekt pri ďalšom otvorení
			onSuccess()
			dispatch(initialize(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM, {
				...transaction
			}))
		}, () => {
			// zlyhanie: odomkneme, aby používateľ mohol skúsiť znova
			isSubmittingRef.current = false
			setIsSubmitting(false)
		}))
	}

	const handleSubmit = (values:ICreateStoreItemTransactionForm) => {
		// tvrdá zábrana proti dvojkliku / dvojitému odoslaniu (tlačidlo + Enter) - inak by vznikli 2 transakcie
		if (isSubmittingRef.current) {
			return
		}
		isSubmittingRef.current = true
		setIsSubmitting(true)

		if (isViaStoreMode) {
			handleCreateViaStore(values)
			return
		}

		const body: ICreateStoreItemTransactionForm = {
			amount: values?.amount,
			type: values.type,
			storeItemID: values.storeItemID,
			// pri "Pripísať" neexistuje odosielateľ, pri "Odpísať" príjemca
			jobFromID: values.type === STORE_ITEM_TRANSACTION_TYPE.IN ? null : values.jobFromID,
			jobToID: values.type === STORE_ITEM_TRANSACTION_TYPE.CONSUME ? null : values.jobToID
		}

		handleCreate({
			...body
		})
	}

	return 	<Modal
		closeIcon={<CloseIcon width={18} height={18}/>}
		title={
			<span>{translateStoreItemTitleTransactionTypeToSk(transactionType)} : {transaction?.storeItemName}   <span style={{ fontSize: '0.5rem' }}>({transaction?.storeItemID})</span></span>
		}
		centered
		visible={isVisible}
		width={width}
		onCancel={onCancel}
		footer={[
			<Button style={{ width: 118, height: 40 }} key='back' type='text' onClick={onCancel}>
				{t('loc:Zrušiť')}
			</Button>,
			<Button style={{ width: 118, height: 40 }} className={classNameBtn || ''} key='submit' type='primary' loading={isSubmitting} disabled={isSubmitting} onClick={() => dispatch(submit(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM))}>
				{t('loc:Vytvoriť')}
			</Button>
		]}
	>
		<div>
			<CreateStoreItemTransactionForm
				onSubmit={ handleSubmit }
				isCreate={ false }
				onTransactionTypeChanged={ (type) => { setTransactionType(type) } }
				contextStore={ contextStore }
				lockedSide={ lockedSide }
				isViaStoreMode={ isViaStoreMode }
				onSwap={ handleSwap }
				onUnlockRequest={ handleUnlockRequest }
				onRelock={ handleRelock }
			/>
		</div>
	</Modal>
}

export default CreateStoreItemTransactionModal
